import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './PourSessionPanel.css';

/**
 * Pour session controls -- start, pause, resume, stop against a blend sheet.
 *
 * WHY THIS EXISTS: pouring had no timing at all. It recorded scan_out only with no
 * scan_in to measure against, every one of 347 events was attributed to the literal
 * operator "Pouring Station", and blends.completed_at was null on all 254 rows.
 *
 * Pouring is also not per-cart work -- it runs continuously from a melter across
 * many carts -- which is why per-cart scan timing never fitted it. The blend sheet
 * already carries a scannable id (BLEND-0260), so the sheet is the work unit.
 *
 * PAUSE IS THE POINT. It lets an operator declare "I was not pouring" -- waiting on
 * wax, waiting on carts, melter not at temp -- so that gap is not charged against
 * their rate. That makes the measurement fair rather than punitive, and the reasons
 * add up to a bottleneck report the shop cannot produce today.
 */

const fmtClock = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function PourSessionPanel({ stationCode, operator, sessionId, onMessage }) {
  const [pour, setPour] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [showPauseReasons, setShowPauseReasons] = useState(false);
  const [showStop, setShowStop] = useState(false);
  const [unitsPoured, setUnitsPoured] = useState('');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const say = useCallback((msg) => { if (onMessage) onMessage(msg); }, [onMessage]);

  const loadPour = useCallback(async () => {
    if (!stationCode) return;
    try {
      const { data } = await axios.get(
        `/api/station-floor/pour/current?station=${encodeURIComponent(stationCode)}`
      );
      setPour(data || null);
    } catch (err) {
      console.error('[pour] could not load current pour:', err.message);
    }
  }, [stationCode]);

  useEffect(() => { loadPour(); }, [loadPour]);

  useEffect(() => {
    axios.get('/api/station-floor/pour/reasons')
      .then(({ data }) => setReasons(data || []))
      .catch(() => {});
  }, []);

  // Keep the elapsed clock honest without refetching every second.
  useEffect(() => {
    if (!pour || pour.status !== 'running') return;
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, [pour]);

  const act = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await loadPour();
    } catch (err) {
      say(`✗ ${err.response?.data?.error || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const pause = (reason) =>
    act(async () => {
      await axios.post(`/api/station-floor/pour/${pour.id}/pause`, {
        reason,
        operator_id: operator?.id ?? null,
        operator_name: operator?.display_name || null
      });
      setShowPauseReasons(false);
      say(`Paused: ${reason}`);
    });

  const resume = () =>
    act(async () => {
      await axios.post(`/api/station-floor/pour/${pour.id}/resume`, {
        operator_id: operator?.id ?? null,
        operator_name: operator?.display_name || null
      });
      say('✓ Pouring resumed');
    });

  const stop = () =>
    act(async () => {
      await axios.post(`/api/station-floor/pour/${pour.id}/stop`, {
        units_poured: unitsPoured === '' ? null : parseInt(unitsPoured, 10),
        operator_id: operator?.id ?? null,
        operator_name: operator?.display_name || null
      });
      setShowStop(false);
      setUnitsPoured('');
      say('✓ Pour complete');
      setPour(null);
    });

  if (!pour) {
    return (
      <div className="pour-panel idle">
        <div className="pour-idle-title">No pour in progress</div>
        <div className="pour-idle-hint">
          Scan a blend sheet to start pouring
          {stationCode ? ` at ${stationCode}` : ''}.
        </div>
      </div>
    );
  }

  const paused = pour.status === 'paused';
  const elapsed = Number(pour.elapsed_minutes || 0);
  const pausedMin = Number(pour.paused_minutes || 0);
  const activeMin = Number(pour.active_minutes || 0);

  return (
    <div className={`pour-panel ${paused ? 'paused' : 'running'}`} data-tick={tick}>
      <div className="pour-head">
        <div>
          <div className="pour-blend">{pour.blend_number}</div>
          <div className="pour-meta">
            {pour.melter_id ? `Melter ${pour.melter_id}` : 'No melter'}
            {pour.station_code ? ` · ${pour.station_code}` : ''}
          </div>
        </div>
        <div className={`pour-state ${paused ? 'paused' : 'running'}`}>
          {paused ? 'PAUSED' : 'POURING'}
        </div>
      </div>

      <div className="pour-clocks">
        <div className="pour-clock">
          <span className="pour-clock-value">{fmtClock(activeMin)}</span>
          <span className="pour-clock-label">Active</span>
        </div>
        <div className="pour-clock">
          <span className="pour-clock-value">{fmtClock(pausedMin)}</span>
          <span className="pour-clock-label">Paused</span>
        </div>
        <div className="pour-clock">
          <span className="pour-clock-value">{fmtClock(elapsed)}</span>
          <span className="pour-clock-label">Total</span>
        </div>
      </div>

      {showPauseReasons ? (
        <div className="pour-reasons">
          <div className="pour-reasons-title">Why are you stopping?</div>
          <div className="pour-reasons-grid">
            {reasons.map((r) => (
              <button
                key={r.code}
                className="pour-reason-btn"
                disabled={busy}
                onClick={() => pause(r.label)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button className="pour-cancel" onClick={() => setShowPauseReasons(false)}>
            Never mind
          </button>
        </div>
      ) : showStop ? (
        <div className="pour-stop">
          <div className="pour-reasons-title">Units poured</div>
          <input
            type="number"
            inputMode="numeric"
            className="pour-units-input"
            value={unitsPoured}
            onChange={(e) => setUnitsPoured(e.target.value)}
            placeholder="e.g. 264"
            autoFocus
          />
          <div className="pour-stop-actions">
            <button className="pour-btn stop" disabled={busy} onClick={stop}>
              Finish pour
            </button>
            <button className="pour-cancel" onClick={() => setShowStop(false)}>
              Never mind
            </button>
          </div>
        </div>
      ) : (
        <div className="pour-actions">
          {paused ? (
            <button className="pour-btn resume" disabled={busy} onClick={resume}>
              Resume pouring
            </button>
          ) : (
            <button
              className="pour-btn pause"
              disabled={busy}
              onClick={() => setShowPauseReasons(true)}
            >
              Pause
            </button>
          )}
          <button className="pour-btn stop" disabled={busy} onClick={() => setShowStop(true)}>
            Stop
          </button>
        </div>
      )}

      {paused && pour.pause_reasons && (
        <div className="pour-pause-note">Paused for: {pour.pause_reasons}</div>
      )}
    </div>
  );
}

export default PourSessionPanel;
